import React from 'react';
import { ArrowLeft, Camera, MapPin, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Datenschutz() {
  const navigate = useNavigate();

  React.useEffect(() => { document.title = 'Datenschutz & DSGVO | 366Transfer Flughafentransfer'; }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="datenschutz-page">
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="datenschutz-back-btn">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <a href="/" className="flex items-baseline gap-0.5">
            <span className="text-2xl font-extrabold text-orange-500">366</span>
            <span className="text-base font-semibold text-slate-800">Transfer</span>
          </a>
        </div>
      </nav>

      {/* Hero header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white py-12 px-6 text-center">
        <h1 className="text-3xl font-bold tracking-wide">Datenschutzerklärung</h1>
        <p className="text-amber-400 text-sm mt-2 uppercase tracking-widest">366 Transfer · Mobile App</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-9 -mt-8 mb-12 bg-white rounded-xl shadow-lg border border-slate-100 py-10 px-6 sm:py-12 sm:px-10">
        <p className="text-base text-slate-600 leading-relaxed">
          Diese Datenschutzerklärung (im Folgenden „Datenschutzerklärung") informiert Sie darüber,
          wie die mobile App <strong className="text-slate-800">366Transfer</strong> (im Folgenden „die Anwendung")
          personenbezogene Daten erhebt, verwendet, speichert und schützt. Verantwortlich ist
          <strong className="text-slate-800"> 366Transfer OÜ</strong> (im Folgenden „Diensteanbieter")
          als kostenloser Dienst.
        </p>

        <p className="mt-4 text-slate-700 leading-relaxed">
          Mit dem Herunterladen oder der Nutzung der Anwendung stimmen Sie automatisch den Bedingungen
          dieser Datenschutzerklärung zu. Bitte lesen Sie diese sorgfältig, bevor Sie die Anwendung nutzen.
        </p>

        <Section title="1. Welche Daten werden erhoben?">
          <p>Die Anwendung kann beim Download und bei der Nutzung folgende Daten erfassen, die Sie selbst angeben:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-3">
            <li><strong>Kontodaten:</strong> Name, E-Mail-Adresse, Passwort (verschlüsselt), Rolle (Kunde / Fahrer / Administrator)</li>
            <li><strong>Kontaktdaten:</strong> Telefonnummer, Anschrift</li>
            <li><strong>Buchungsdaten:</strong> Abholort, Zielort, Datum, Uhrzeit, Anzahl der Passagiere, Sondergepäck</li>
            <li><strong>Zahlungsdaten:</strong> Werden ausschließlich von unseren Zahlungsdienstleistern Stripe und PayPal verarbeitet — wir speichern keine vollständigen Kreditkartennummern oder Bankverbindungen auf unseren Servern</li>
            <li><strong>Standortdaten:</strong> Annähernder oder genauer Standort (nur mit Ihrer Einwilligung, zur Routenberechnung und Adresssuche)</li>
            <li><strong>Fahrer-Dokumente (nur für Fahrer):</strong> Führerschein, Personalausweis, Fahrzeugschein, Versicherungsnachweis, Profilfoto</li>
            <li><strong>Geräteinformationen:</strong> Geräte-ID, IP-Adresse, Betriebssystem, Browsertyp, Spracheinstellung (für Diagnose und Diensterbringung)</li>
          </ul>
        </Section>

        <Section title="2. Berechtigungen der Anwendung">
          <p>
            Damit bestimmte Funktionen verfügbar sind, fordert die Anwendung folgende Berechtigungen an.
            Sie können diese jederzeit in den Einstellungen Ihres Geräts widerrufen.
          </p>

          <PermBox icon={<Camera size={18} className="text-blue-700" />} title="Kameraberechtigung (CAMERA)">
            <p>Die Anwendung benötigt Zugriff auf die Kamera Ihres Geräts ausschließlich für folgenden Zweck:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Während der <strong>Fahrer-Registrierung</strong> können Fahrer ihre Dokumente (Führerschein, Personalausweis, Fahrzeugschein, Versicherungsnachweis, Profilfoto) <strong>direkt mit der Kamera fotografieren</strong> und sicher hochladen.</li>
            </ul>
            <p className="mt-3">
              Die Kamera wird <strong>ausschließlich auf aktive Anfrage des Nutzers</strong> aktiviert
              (durch Antippen des Buttons „Kamera"). Es werden <strong>keine Fotos oder Videos
              automatisch oder im Hintergrund</strong> aufgenommen. Die aufgenommenen Bilder werden
              ausschließlich verschlüsselt (HTTPS) an unseren Server <em>366transfer.com</em> übertragen
              und ausschließlich zur Verifizierung Ihres Fahrer-Accounts verwendet. Die Bilder werden
              <strong> nicht an Dritte weitergegeben</strong>.
            </p>
            <p className="mt-3">
              Sie können die Kameraberechtigung jederzeit in den Geräte-Einstellungen unter
              <em> „Apps → 366Transfer → Berechtigungen"</em> widerrufen. Ohne diese Berechtigung können
              Sie die App weiterhin nutzen, müssen Dokumente jedoch über die Galerie oder als Datei hochladen.
            </p>
          </PermBox>

          <PermBox icon={<MapPin size={18} className="text-blue-700" />} title="Standortberechtigung (ACCESS_FINE_LOCATION / ACCESS_COARSE_LOCATION)">
            <p>Die Standortberechtigung wird verwendet, um:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Die Adresssuche bei der Buchung zu erleichtern (Vorschläge in Ihrer Nähe)</li>
              <li>Fahrer bei aktiven Aufträgen zu navigieren</li>
            </ul>
            <p className="mt-3">
              Der Standort wird ausschließlich aktiv abgefragt, wenn Sie eine ortsbezogene Funktion
              nutzen, und wird nicht im Hintergrund verfolgt.
            </p>
          </PermBox>

          <PermBox icon={<ImageIcon size={18} className="text-blue-700" />} title="Speicher-/Galeriezugriff (READ_EXTERNAL_STORAGE)">
            <p>
              Wird benötigt, damit Sie Dokumente und Fotos aus Ihrer Galerie auswählen und hochladen
              können (alternativ zur Kamera).
            </p>
          </PermBox>
        </Section>

        <Section title="3. Wofür werden die Daten verwendet?">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Bereitstellung des Buchungsdienstes und der Fahrer-Vermittlung</li>
            <li>Verifizierung der Fahrer-Identität (Dokumentenprüfung)</li>
            <li>Abwicklung von Zahlungen über Stripe und PayPal</li>
            <li>Kommunikation mit Ihnen bezüglich Buchungen und Account-Status</li>
            <li>Verbesserung und Fehlerbehebung in der Anwendung</li>
            <li>Erfüllung gesetzlicher Verpflichtungen (z. B. Steuerrecht, Buchhaltung)</li>
          </ul>
        </Section>

        <Section title="4. Drittanbieter-Dienste">
          <p>Die Anwendung verwendet folgende Drittanbieter-Dienste, die ggf. eigene Daten erfassen:</p>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="text-left p-2.5 border-b border-slate-200">Dienst</th>
                  <th className="text-left p-2.5 border-b border-slate-200">Zweck</th>
                  <th className="text-left p-2.5 border-b border-slate-200">Datenschutzerklärung</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Google Play-Dienste', 'App-Auslieferung, Updates', 'https://policies.google.com/privacy'],
                  ['Expo', 'App-Build & Updates', 'https://expo.io/privacy'],
                  ['Stripe', 'Kreditkartenzahlung', 'https://stripe.com/de/privacy'],
                  ['PayPal', 'Online-Zahlung', 'https://www.paypal.com/de/webapps/mpp/ua/privacy-full'],
                  ['Photon (OpenStreetMap)', 'Adresssuche', 'https://www.openstreetmap.org/copyright'],
                  ['OSRM', 'Routenberechnung', 'https://project-osrm.org/'],
                ].map(([name, purpose, url]) => (
                  <tr key={name} className="border-b border-slate-100">
                    <td className="p-2.5">{name}</td>
                    <td className="p-2.5 text-slate-600">{purpose}</td>
                    <td className="p-2.5"><a href={url} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Link</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="5. Datenspeicherung und Sicherheit">
          <p>
            Personenbezogene Daten werden auf gesicherten Servern in der Europäischen Union gespeichert.
            Sämtliche Datenübertragungen erfolgen verschlüsselt über <strong>HTTPS / TLS 1.2+</strong>.
            Passwörter werden ausschließlich als <strong>bcrypt-Hash</strong> gespeichert — niemals im Klartext.
          </p>
        </Section>

        <Section title="6. Speicherdauer">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Konto- und Buchungsdaten: solange Ihr Account aktiv ist, oder gemäß gesetzlichen Aufbewahrungsfristen (max. 10 Jahre für steuerrelevante Belege).</li>
            <li>Fahrer-Dokumente: bis zur Beendigung Ihres Fahrer-Status, danach Löschung innerhalb von 90 Tagen.</li>
            <li>Diagnose- und Logdaten: max. 30 Tage.</li>
          </ul>
        </Section>

        <Section title="7. Ihre Rechte (DSGVO)">
          <p>Sie haben jederzeit das Recht auf:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-2">
            <li><strong>Auskunft</strong> über die zu Ihrer Person gespeicherten Daten (Art. 15 DSGVO)</li>
            <li><strong>Berichtigung</strong> unrichtiger Daten (Art. 16 DSGVO)</li>
            <li><strong>Löschung</strong> Ihrer Daten („Recht auf Vergessenwerden", Art. 17 DSGVO)</li>
            <li><strong>Einschränkung</strong> der Verarbeitung (Art. 18 DSGVO)</li>
            <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
            <li><strong>Widerspruch</strong> gegen die Verarbeitung (Art. 21 DSGVO)</li>
            <li><strong>Beschwerde</strong> bei einer Aufsichtsbehörde</li>
          </ul>
          <p className="mt-3">
            Um diese Rechte auszuüben, kontaktieren Sie uns bitte unter{' '}
            <a href="mailto:info@366transfer.com" className="text-orange-500 hover:underline">info@366transfer.com</a>.
          </p>
        </Section>

        <Section title="8. Daten von Kindern">
          <p>
            Die Anwendung richtet sich nicht an Personen unter 16 Jahren. Wir erheben wissentlich keine
            Daten von Kindern unter 16 Jahren. Sollten wir feststellen, dass derartige Daten erfasst wurden,
            werden diese unverzüglich gelöscht.
          </p>
        </Section>

        <Section title="9. Internetverbindung">
          <p>
            Einige Funktionen der Anwendung erfordern eine aktive Internetverbindung. Der Diensteanbieter
            haftet nicht, wenn die Anwendung aufgrund mangelnder Internetverbindung nicht in vollem Umfang
            funktioniert. Bei Nutzung außerhalb eines WLAN-Bereichs gelten die Bedingungen Ihres Mobilfunkanbieters.
          </p>
        </Section>

        <Section title="10. Änderungen dieser Datenschutzerklärung">
          <p>
            Der Diensteanbieter kann diese Datenschutzerklärung von Zeit zu Zeit aktualisieren. Wir empfehlen
            Ihnen, diese Seite regelmäßig auf Änderungen zu überprüfen. Wesentliche Änderungen werden Ihnen
            innerhalb der App oder per E-Mail mitgeteilt.
          </p>
        </Section>

        <div className="mt-6 bg-amber-50 border-l-4 border-amber-400 px-5 py-3 rounded-r-md text-sm">
          <strong>Gültig ab:</strong> 27. April 2026
        </div>

        <Section title="11. Kontakt">
          <p>Bei Fragen oder Anregungen zu dieser Datenschutzerklärung wenden Sie sich bitte an:</p>
          <p className="mt-3">
            <strong className="text-slate-800">366Transfer OÜ</strong><br />
            E-Mail: <a href="mailto:info@366transfer.com" className="text-orange-500 hover:underline">info@366transfer.com</a><br />
            Web: <a href="https://366transfer.com" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">https://366transfer.com</a>
          </p>
        </Section>

        <div className="mt-10 pt-5 border-t border-slate-200 text-center text-xs text-slate-400">
          © 2026 366Transfer OÜ. Alle Rechte vorbehalten. ·{' '}
          <a href="/agb" className="text-slate-500 hover:text-orange-500 hover:underline">Allgemeine Geschäftsbedingungen</a>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-bold text-slate-800 border-l-4 border-amber-400 pl-3 mb-3">{title}</h2>
      <div className="text-slate-700 leading-relaxed text-sm sm:text-[15px] space-y-2">
        {children}
      </div>
    </section>
  );
}

function PermBox({ icon, title, children }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-4 my-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <strong className="text-slate-800">{title}</strong>
      </div>
      <div className="text-slate-700 text-sm space-y-2">{children}</div>
    </div>
  );
}
